<template>
  <v-container id="departments" fluid tag="section" class="dp-page dashboard-container" style="min-height: 100vh;">

    <!-- ── Cabeçalho ─────────────────────────────── -->
    <div class="d-flex align-center justify-space-between mb-5">
      <div class="d-flex align-center">
        <div class="dp-header-icon mr-3">
          <v-icon color="#22d3ee" size="22">mdi-domain</v-icon>
        </div>
        <div>
          <div class="dp-page-title">{{ $t('departments.title') }}</div>
          <div class="dp-page-sub">{{ departments.length }} departamentos · crie, edite e organize a estrutura</div>
        </div>
      </div>
      <v-btn v-if="!isViewOnlyMode" depressed class="dp-btn-new text-none" @click="openCreateDepartmentDialog">
        <v-icon left small>mdi-plus</v-icon>
        {{ $t('departments.new') }}
      </v-btn>
    </div>

    <!-- ── Filtros ─────────────────────────────────── -->
    <div class="d-flex align-center mb-4 dp-filters">
      <v-text-field
        v-model="search"
        prepend-inner-icon="mdi-magnify"
        :placeholder="$t('departments.searchPlaceholder')"
        outlined dense hide-details clearable
        class="dp-search mr-3"
      />
      <v-select
        v-model="filterStatus"
        :items="statusOptions"
        item-text="text"
        item-value="value"
        label="Status"
        outlined dense hide-details clearable
        class="dp-filter mr-3"
        append-icon="mdi-chevron-down"
      />
      <v-btn outlined class="dp-btn-clear text-none" @click="clearFilters">
        <v-icon left small>mdi-reload</v-icon>
        Limpar
      </v-btn>
    </div>

    <!-- ── Tabela (sem card) ───────────────────────── -->
    <v-data-table
      :headers="headers"
      :items="filteredDepartments"
      :loading="loading"
      :search="search"
      class="dp-table elevation-0"
      item-key="id"
      show-expand
      single-expand
      :expanded.sync="expanded"
    >
      <!-- Nome -->
      <template v-slot:item.name="{ item }">
        <div class="d-flex align-center py-2">
          <div class="dp-dept-icon mr-3" :style="{ backgroundColor: getDeptColor(item) + '22', border: '1px solid ' + getDeptColor(item) + '55' }">
            <v-icon size="15" :color="getDeptColor(item)">mdi-domain</v-icon>
          </div>
          <div>
            <div class="dp-dept-name">{{ item.name }}</div>
            <div class="dp-dept-sub">{{ item.description || 'Sem descrição' }}</div>
          </div>
        </div>
      </template>

      <!-- Responsável -->
      <template v-slot:item.managerName="{ item }">
        <div v-if="item.managerName" class="d-flex align-center">
          <v-avatar size="26" color="primary" class="mr-2" style="flex-shrink:0;">
            <v-img v-if="item.managerAvatarUrl" :src="item.managerAvatarUrl" />
            <span v-else class="white--text" style="font-size:10px; font-weight:700;">{{ getInitials(item.managerName) }}</span>
          </v-avatar>
          <span class="dp-resp-name">{{ item.managerName }}</span>
        </div>
        <span v-else class="dp-empty">—</span>
      </template>

      <!-- Quantidade de Usuários -->
      <template v-slot:item.userCount="{ item }">
        <div class="d-flex align-center">
          <v-icon size="14" class="mr-1 dp-count-icon">mdi-account-multiple</v-icon>
          <span class="dp-count-value">{{ item.userCount || 0 }}</span>
        </div>
      </template>

      <!-- Status -->
      <template v-slot:item.active="{ item }">
        <div class="d-flex align-center">
          <span class="dp-status-dot mr-1" :style="{ backgroundColor: item.active ? '#22c55e' : '#6b7280' }"></span>
          <span class="dp-status-text" :style="{ color: item.active ? '#4ade80' : '#9ca3af' }">
            {{ item.active ? 'Ativo' : 'Inativo' }}
          </span>
        </div>
      </template>

      <!-- Criado em -->
      <template v-slot:item.createdAt="{ item }">
        <span class="dp-date">{{ formatDate(item.createdAt) }}</span>
      </template>

      <!-- Ações -->
      <template v-slot:item.actions="{ item }">
        <div class="d-flex align-center justify-end" style="gap:4px;">
          <v-switch
            :key="item.id + '-' + toggleRefreshKey"
            :input-value="item.active"
            color="#FF771C"
            dense
            hide-details
            class="dp-toggle mt-0 pt-0"
            :disabled="isViewOnlyMode"
            @change="toggleDepartmentStatus(item)"
          />
          <v-btn icon x-small class="dp-action-btn" :disabled="isViewOnlyMode" @click="editDepartment(item)">
            <v-icon size="15">mdi-pencil-outline</v-icon>
          </v-btn>
        </div>
      </template>

      <!-- Expand chevron -->
      <template v-slot:item.data-table-expand="{ item, isExpanded }">
        <v-menu offset-y left>
          <template v-slot:activator="{ on, attrs }">
            <v-btn icon x-small class="dp-action-btn" v-bind="attrs" v-on="on">
              <v-icon size="15">{{ isExpanded ? 'mdi-chevron-up' : 'mdi-chevron-down' }}</v-icon>
            </v-btn>
          </template>
          <v-list dense class="py-1">
            <v-list-item @click="expandDepartment(item)">
              <v-list-item-icon class="mr-2"><v-icon small>mdi-briefcase-outline</v-icon></v-list-item-icon>
              <v-list-item-title class="text-body-2">Cargos</v-list-item-title>
            </v-list-item>
            <v-list-item @click="showDepartmentUsers(item)">
              <v-list-item-icon class="mr-2"><v-icon small>mdi-account-group-outline</v-icon></v-list-item-icon>
              <v-list-item-title class="text-body-2">Usuários</v-list-item-title>
            </v-list-item>
          </v-list>
        </v-menu>
      </template>

      <!-- Expansão: Cargos -->
      <template v-slot:expanded-item="{ headers, item }">
        <td :colspan="headers.length" class="pa-0">
          <div class="dp-expanded-panel">
            <div class="d-flex align-center justify-space-between px-5 py-3">
              <div class="d-flex align-center">
                <v-icon small color="#22d3ee" class="mr-2">mdi-briefcase-outline</v-icon>
                <span class="dp-expanded-title">Cargos de {{ item.name }}</span>
              </div>
              <v-btn v-if="!isViewOnlyMode" depressed x-small class="dp-btn-new text-none" @click="openCreateJobRoleDialog(item)">
                <v-icon left x-small>mdi-plus</v-icon>
                Novo Cargo
              </v-btn>
            </div>
            <div v-if="item.loadingJobRoles" class="text-center py-6">
              <v-progress-circular indeterminate color="#22d3ee" size="28" />
            </div>
            <v-data-table
              v-else
              :headers="jobRoleHeaders"
              :items="item.jobRoles || []"
              class="dp-jobrole-table elevation-0"
              dense
              hide-default-footer
              :items-per-page="-1"
            >
              <template v-slot:item.title="{ item: jobRole }">
                <div class="d-flex align-center">
                  <v-icon size="13" color="#a855f7" class="mr-2">mdi-briefcase-outline</v-icon>
                  <span class="text-body-2">{{ jobRole.title }}</span>
                </div>
              </template>
              <template v-slot:item.actions="{ item: jobRole }">
                <div class="d-flex align-center justify-center" style="gap:2px;">
                  <v-btn icon x-small class="dp-action-btn" :disabled="isViewOnlyMode" @click="editJobRole(item, jobRole)">
                    <v-icon size="14">mdi-pencil-outline</v-icon>
                  </v-btn>
                  <v-btn icon x-small class="dp-action-btn dp-action-btn--delete" :disabled="isViewOnlyMode" @click="deleteJobRole(item, jobRole)">
                    <v-icon size="14">mdi-delete-outline</v-icon>
                  </v-btn>
                </div>
              </template>
              <template v-slot:no-data>
                <div class="text-center py-4 dp-empty">Nenhum cargo cadastrado</div>
              </template>
            </v-data-table>
          </div>
        </td>
      </template>

      <!-- No data -->
      <template v-slot:no-data>
        <div class="text-center py-12">
          <v-icon size="52" style="color:rgba(255,255,255,0.08)">mdi-domain</v-icon>
          <div class="dp-empty mt-3 mb-4">Nenhum departamento encontrado</div>
          <v-btn v-if="!isViewOnlyMode" depressed class="dp-btn-new text-none" @click="openCreateDepartmentDialog">
            <v-icon left small>mdi-plus</v-icon>
            Criar Departamento
          </v-btn>
        </div>
      </template>
    </v-data-table>

    <!-- Dialog: Criar/Editar Departamento -->
    <v-dialog
      v-model="departmentDialog"
      max-width="600px"
      persistent
    >
      <v-card>
        <v-card-title class="headline">
          <v-icon left color="primary">mdi-domain</v-icon>
          {{ editingDepartment ? 'Editar Departamento' : 'Novo Departamento' }}
        </v-card-title>

        <v-card-text>
          <v-form ref="departmentForm" v-model="validDepartment">
            <v-row>
              <v-col cols="12">
                <v-text-field
                  v-model="departmentData.name"
                  label="Nome do Departamento *"
                  :rules="[rules.required]"
                  outlined
                  dense
                  prepend-inner-icon="mdi-domain"
                />
              </v-col>

              <v-col cols="12">
                <v-textarea
                  v-model="departmentData.description"
                  label="Descrição ou Responsabilidades"
                  outlined
                  dense
                  rows="3"
                  prepend-inner-icon="mdi-text"
                  placeholder="Ex: Responsável pelo desenvolvimento e manutenção dos produtos digitais"
                />
              </v-col>

              <v-col cols="12">
                <v-autocomplete
                  v-model="departmentData.managerId"
                  :items="managers"
                  item-text="name"
                  item-value="id"
                  label="Gestor Responsável *"
                  :rules="[rules.required]"
                  :loading="loadingManagers"
                  outlined
                  dense
                  prepend-inner-icon="mdi-account-tie"
                  placeholder="Selecione o gestor responsável"
                  clearable
                >
                  <template v-slot:item="{ item }">
                    <v-list-item-avatar color="primary">
                      <span class="white--text">{{ getInitials(item.name) }}</span>
                    </v-list-item-avatar>
                    <v-list-item-content>
                      <v-list-item-title>{{ item.name }}</v-list-item-title>
                      <v-list-item-subtitle>{{ item.email }}</v-list-item-subtitle>
                    </v-list-item-content>
                  </template>
                </v-autocomplete>
              </v-col>

              <v-col cols="12">
                <v-switch
                  v-model="departmentData.active"
                  label="Departamento Ativo"
                  color="#FF771C"
                  inset
                  :hint="departmentData.active ? 'Departamento ativo e visível' : 'Departamento desativado'"
                  persistent-hint
                />
              </v-col>

              <!-- Cargos Iniciais (obrigatório ao criar) -->
              <v-col v-if="!editingDepartment" cols="12">
                <v-divider class="my-4" />
                <div class="text-h6 mb-3">
                  <v-icon left color="purple">mdi-briefcase</v-icon>
                  Cargos do Departamento *
                </div>
                <div class="text-caption grey--text mb-4">
                  Adicione pelo menos 1 cargo para este departamento
                </div>

                <!-- Lista de cargos adicionados -->
                <v-card
                  v-for="(jobRole, index) in departmentData.initialJobRoles"
                  :key="index"
                  outlined
                  class="mb-2"
                >
                  <v-card-text class="py-2 px-3">
                    <!-- Modo Visualização -->
                    <div v-if="editingJobRoleIndex !== index" class="d-flex align-center">
                      <v-icon color="purple" class="mr-2" small>mdi-briefcase</v-icon>
                      <span class="font-weight-medium">{{ jobRole }}</span>
                      <v-spacer />
                      <v-btn
                        icon
                        x-small
                        color="primary"
                        @click="startEditJobRole(index)"
                      >
                        <v-icon small>mdi-pencil</v-icon>
                      </v-btn>
                      <v-btn
                        icon
                        x-small
                        color="error"
                        class="ml-1"
                        @click="removeInitialJobRole(index)"
                      >
                        <v-icon small>mdi-delete</v-icon>
                      </v-btn>
                    </div>

                    <!-- Modo Edição -->
                    <div v-else class="d-flex align-center">
                      <v-text-field
                        v-model="editingJobRoleTitle"
                        dense
                        hide-details
                        autofocus
                        @keyup.enter="saveEditJobRole(index)"
                        @keyup.esc="cancelEditJobRole"
                      />
                      <v-btn
                        icon
                        x-small
                        color="success"
                        class="ml-1"
                        :disabled="!editingJobRoleTitle || !editingJobRoleTitle.trim()"
                        @click="saveEditJobRole(index)"
                      >
                        <v-icon small>mdi-check</v-icon>
                      </v-btn>
                      <v-btn
                        icon
                        x-small
                        color="grey"
                        class="ml-1"
                        @click="cancelEditJobRole"
                      >
                        <v-icon small>mdi-close</v-icon>
                      </v-btn>
                    </div>
                  </v-card-text>
                </v-card>

                <!-- Adicionar novo cargo -->
                <div class="d-flex align-center mt-3" style="gap: 12px">
                  <v-text-field
                    v-model="newJobRoleTitle"
                    label="Nome do Cargo"
                    outlined
                    dense
                    prepend-inner-icon="mdi-briefcase"
                    placeholder="Ex: Desenvolvedor Sênior"
                    class="flex-grow-1"
                    hide-details
                    @keyup.enter="addInitialJobRole"
                  />
                  <v-btn
                    color="purple"
                    :disabled="!newJobRoleTitle || !newJobRoleTitle.trim()"
                    @click="addInitialJobRole"
                  >
                    <v-icon left>mdi-plus</v-icon>
                    Adicionar
                  </v-btn>
                </div>

                <v-alert
                  v-if="departmentData.initialJobRoles.length === 0"
                  type="warning"
                  outlined
                  dense
                  class="mt-4 mb-0"
                  color="orange darken-1"
                >
                  <div class="d-flex align-center">
                    <v-icon small color="orange darken-1" class="mr-2">mdi-alert-circle-outline</v-icon>
                    <span class="text-body-2">É obrigatório adicionar pelo menos 1 cargo</span>
                  </div>
                </v-alert>
              </v-col>
            </v-row>
          </v-form>
        </v-card-text>

        <v-card-actions>
          <v-spacer />
          <v-btn
            text
            @click="closeDepartmentDialog"
          >
            Cancelar
          </v-btn>
          <v-btn
            color="#FF771C"
            :loading="saving"
            :disabled="!validDepartment || isViewOnlyMode || (!editingDepartment && departmentData.initialJobRoles.length === 0)"
            @click="saveDepartment"
          >
            <v-icon left>mdi-content-save</v-icon>
            Salvar
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Dialog: Criar/Editar Cargo -->
    <v-dialog
      v-model="jobRoleDialog"
      max-width="500px"
      persistent
    >
      <v-card>
        <v-card-title class="headline">
          <v-icon left color="purple">mdi-briefcase</v-icon>
          {{ editingJobRole ? 'Editar Cargo' : 'Novo Cargo' }}
        </v-card-title>

        <v-card-text>
          <v-alert
            type="info"
            text
            dense
            class="mb-4"
          >
            Departamento: <strong>{{ selectedDepartment?.name }}</strong>
          </v-alert>

          <v-form ref="jobRoleForm" v-model="validJobRole">
            <v-text-field
              v-model="jobRoleData.title"
              label="Título do Cargo *"
              :rules="[rules.required]"
              outlined
              dense
              prepend-inner-icon="mdi-briefcase-outline"
              placeholder="Ex: Desenvolvedor Backend Sênior"
            />
          </v-form>
        </v-card-text>

        <v-card-actions>
          <v-spacer />
          <v-btn
            text
            @click="closeJobRoleDialog"
          >
            Cancelar
          </v-btn>
          <v-btn
            color="#FF771C"
            :loading="savingJobRole"
            :disabled="!validJobRole || isViewOnlyMode"
            @click="saveJobRole"
          >
            <v-icon left>mdi-content-save</v-icon>
            Salvar
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Dialog: Movimentação de Usuários ao Desativar Departamento -->
    <v-dialog
      v-model="moveUsersDialog"
      max-width="800px"
      persistent
      scrollable
    >
      <v-card>
        <v-card-title class="headline orange darken-1 white--text">
          <v-icon left color="white">mdi-account-switch</v-icon>
          Movimentar Usuários do Departamento
        </v-card-title>

        <v-card-subtitle class="pt-3 pb-0">
          <v-alert
            type="warning"
            text
            dense
            class="mb-0"
          >
            <strong>{{ departmentToDeactivate?.name }}</strong> será desativado.
            <br>Mova os {{ usersToMove.length }} usuário(s) para outro departamento ativo.
          </v-alert>
        </v-card-subtitle>

        <v-card-text class="pt-4" style="max-height: 500px">
          <!-- Loading -->
          <div v-if="loadingUsersToMove" class="text-center pa-8">
            <v-progress-circular
              indeterminate
              color="primary"
              size="50"
            />
            <div class="mt-4 grey--text">Carregando usuários...</div>
          </div>

          <!-- Se não houver usuários -->
          <div v-else-if="usersToMove.length === 0" class="text-center pa-8">
            <v-icon size="64" color="grey lighten-1">mdi-account-off</v-icon>
            <div class="text-h6 mt-4 grey--text">Nenhum usuário neste departamento</div>
            <div class="text-body-2 grey--text">Você pode desativar o departamento sem movimentações.</div>
          </div>

          <!-- Lista de Usuários -->
          <div v-else>
            <!-- Modo de Movimentação -->
            <v-card outlined class="mb-4">
              <v-card-text class="pb-2">
                <v-radio-group
                  v-model="moveMode"
                  row
                  dense
                  hide-details
                  class="mt-0"
                >
                  <v-radio
                    label="Mover todos para o mesmo departamento"
                    value="bulk"
                    color="primary"
                  />
                  <v-radio
                    label="Selecionar departamento individualmente"
                    value="individual"
                    color="primary"
                  />
                </v-radio-group>
              </v-card-text>
            </v-card>

            <!-- Modo: Mover Todos -->
            <v-card v-if="moveMode === 'bulk'" outlined class="mb-4">
              <v-card-text>
                <v-autocomplete
                  v-model="bulkTargetDepartmentId"
                  :items="activeDepartments"
                  item-text="name"
                  item-value="id"
                  label="Departamento Destino *"
                  prepend-inner-icon="mdi-domain"
                  outlined
                  dense
                  clearable
                  :rules="[rules.required]"
                  placeholder="Selecione o departamento para TODOS os usuários"
                >
                  <template v-slot:selection="{ item }">
                    <v-chip small color="primary" dark>
                      <v-icon small left>mdi-domain</v-icon>
                      {{ item.name }}
                    </v-chip>
                  </template>
                  <template v-slot:item="{ item }">
                    <v-list-item-avatar color="primary" size="32">
                      <v-icon color="white" small>mdi-domain</v-icon>
                    </v-list-item-avatar>
                    <v-list-item-content>
                      <v-list-item-title>{{ item.name }}</v-list-item-title>
                      <v-list-item-subtitle class="text-caption">
                        {{ item.userCount || 0 }} usuários
                      </v-list-item-subtitle>
                    </v-list-item-content>
                  </template>
                </v-autocomplete>
              </v-card-text>
            </v-card>

            <!-- Lista de Usuários -->
            <div class="text-subtitle-2 mb-3 font-weight-bold">
              <v-icon small class="mr-1">mdi-account-group</v-icon>
              Usuários ({{ usersToMove.length }})
            </div>

            <!-- Modo Individual: Tabela com Select -->
            <v-simple-table v-if="moveMode === 'individual'" dense>
              <template v-slot:default>
                <thead>
                  <tr>
                    <th>Usuário</th>
                    <th>Cargo</th>
                    <th width="40%">Novo Departamento</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="user in usersToMove" :key="user.id">
                    <td>
                      <div class="d-flex align-center py-2">
                        <v-avatar size="32" class="mr-2" color="primary">
                          <v-img v-if="user.avatarUrl" :src="user.avatarUrl" />
                          <span v-else class="white--text text-caption">
                            {{ getInitials(user.name) }}
                          </span>
                        </v-avatar>
                        <div>
                          <div class="text-body-2 font-weight-medium">{{ user.name }}</div>
                          <div class="text-caption grey--text">{{ user.email }}</div>
                        </div>
                      </div>
                    </td>
                    <td>
                      <div class="text-body-2">
                        {{ user.jobRole?.title || '-' }}
                      </div>
                    </td>
                    <td>
                      <v-select
                        v-model="user.targetDepartmentId"
                        :items="activeDepartments"
                        item-text="name"
                        item-value="id"
                        dense
                        outlined
                        hide-details
                        placeholder="Selecione"
                        class="mt-1 mb-1"
                      >
                        <template v-slot:selection="{ item }">
                          <span class="text-body-2">{{ item.name }}</span>
                        </template>
                        <template v-slot:item="{ item }">
                          <v-list-item-content>
                            <v-list-item-title>
                              {{ item.name }}
                              <v-chip x-small class="ml-2" color="grey lighten-2">
                                {{ item.userCount || 0 }} usuários
                              </v-chip>
                            </v-list-item-title>
                          </v-list-item-content>
                        </template>
                      </v-select>
                    </td>
                  </tr>
                </tbody>
              </template>
            </v-simple-table>

            <!-- Modo Bulk: Lista Simples -->
            <v-list v-else dense class="py-0">
              <v-list-item
                v-for="user in usersToMove"
                :key="user.id"
                class="px-0"
              >
                <v-list-item-avatar size="40">
                  <v-img v-if="user.avatarUrl" :src="user.avatarUrl" />
                  <span v-else class="primary white--text">
                    {{ getInitials(user.name) }}
                  </span>
                </v-list-item-avatar>
                <v-list-item-content>
                  <v-list-item-title class="text-body-2 font-weight-medium">
                    {{ user.name }}
                  </v-list-item-title>
                  <v-list-item-subtitle class="text-caption">
                    {{ user.email }} • {{ user.jobRole?.title || 'Sem cargo' }}
                  </v-list-item-subtitle>
                </v-list-item-content>
                <v-list-item-action>
                  <v-chip x-small color="grey lighten-2">
                    {{ user.department?.name }}
                  </v-chip>
                </v-list-item-action>
              </v-list-item>
            </v-list>
          </div>
        </v-card-text>

        <v-divider />

        <v-card-actions>
          <v-btn
            text
            @click="cancelMoveUsers"
          >
            Cancelar
          </v-btn>
          <v-spacer />
          <v-btn
            v-if="usersToMove.length === 0"
            color="orange darken-1"
            @click="confirmDeactivateWithoutUsers"
          >
            <v-icon left>mdi-power</v-icon>
            Desativar Mesmo Assim
          </v-btn>
          <v-btn
            v-else
            color="primary"
            :loading="movingUsers"
            :disabled="!canConfirmMove"
            @click="confirmMoveUsers"
          >
            <v-icon left>mdi-check</v-icon>
            Confirmar e Desativar
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Dialog: Visualizar Usuários do Departamento -->
    <v-dialog
      v-model="usersDialog"
      max-width="900px"
      scrollable
    >
      <v-card>
        <v-card-title class="headline primary white--text">
          <v-icon left color="white">mdi-account-group</v-icon>
          Usuários do Departamento: {{ selectedDepartmentForUsers?.name }}
        </v-card-title>

        <v-card-text class="pt-4" style="max-height: 600px">
          <!-- Loading -->
          <div v-if="loadingDepartmentUsers" class="text-center pa-8">
            <v-progress-circular
              indeterminate
              color="primary"
              size="50"
            />
            <div class="mt-4 grey--text">Carregando usuários...</div>
          </div>

          <!-- Se não houver usuários -->
          <div v-else-if="departmentUsers.length === 0" class="text-center pa-8">
            <v-icon size="64" color="grey lighten-1">mdi-account-off</v-icon>
            <div class="text-h6 mt-4 grey--text">Nenhum usuário neste departamento</div>
          </div>

          <!-- Lista de Usuários -->
          <v-simple-table v-else dense>
            <template v-slot:default>
              <thead>
                <tr>
                  <th class="text-left">Usuário</th>
                  <th class="text-left">Cargo</th>
                  <th class="text-left">E-mail</th>
                  <th class="text-center">Status</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="user in departmentUsers" :key="user.id">
                  <td>
                    <div class="d-flex align-center py-2">
                      <v-avatar size="32" class="mr-2" color="primary">
                        <v-img v-if="user.avatarUrl" :src="user.avatarUrl" />
                        <span v-else class="white--text text-caption">
                          {{ getInitials(user.name) }}
                        </span>
                      </v-avatar>
                      <div class="font-weight-medium">{{ user.name }}</div>
                    </div>
                  </td>
                  <td>
                    <span class="text-body-2">{{ user.jobRole?.title || '-' }}</span>
                  </td>
                  <td>
                    <span class="text-body-2 grey--text">{{ user.email }}</span>
                  </td>
                  <td class="text-center">
                    <v-chip
                      x-small
                      :color="user.status === 'ACTIVE' ? 'success' : 'grey'"
                      dark
                    >
                      {{ user.status === 'ACTIVE' ? 'Ativo' : 'Inativo' }}
                    </v-chip>
                  </td>
                </tr>
              </tbody>
            </template>
          </v-simple-table>
        </v-card-text>

        <v-divider />

        <v-card-actions>
          <v-spacer />
          <v-btn
            color="primary"
            @click="usersDialog = false"
          >
            Fechar
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Dialog: Confirmar Exclusão de Cargo -->
    <v-dialog
      v-model="deleteJobRoleDialog"
      max-width="500px"
      persistent
    >
      <v-card>
        <v-card-title class="headline error white--text">
          <v-icon left color="white">mdi-alert-circle</v-icon>
          Confirmar Exclusão
        </v-card-title>

        <v-card-text class="pt-4">
          <v-alert
            type="warning"
            text
            dense
            class="mb-3"
          >
            Esta ação não pode ser desfeita!
          </v-alert>

          <div class="text-body-1">
            Tem certeza que deseja excluir o cargo <strong>{{ jobRoleToDelete?.title }}</strong>?
          </div>

          <div class="text-body-2 grey--text mt-3">
            Departamento: <strong>{{ selectedDepartmentForDelete?.name }}</strong>
          </div>
        </v-card-text>

        <v-card-actions>
          <v-spacer />
          <v-btn
            text
            @click="cancelDeleteJobRole"
          >
            Cancelar
          </v-btn>
          <v-btn
            color="error"
            :loading="deletingJobRole"
            @click="confirmDeleteJobRole"
          >
            <v-icon left>mdi-delete</v-icon>
            Excluir
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Snackbar -->
    <v-snackbar
      v-model="snackbar.show"
      :color="snackbar.color"
      :timeout="3000"
      top
    >
      {{ snackbar.text }}
      <template v-slot:action="{ attrs }">
        <v-btn
          text
          v-bind="attrs"
          @click="snackbar.show = false"
        >
          Fechar
        </v-btn>
      </template>
    </v-snackbar>
  </v-container>
</template>

<script>
import OrganizationService from '@/services/organization.service'
import TeamsService from '@/services/teams.service'
import UsersService from '@/services/users.service'

export default {
  name: 'DepartmentsView',

  data: () => ({
    // Listagem Departamentos
    departments: [],
    loading: false,
    search: '',
    filterStatus: null,
    filterTeam: null,
    expanded: [],
    toggleRefreshKey: 0, // Key para forçar re-render do toggle

    // Times (para filtro)
    teams: [],
    loadingTeams: false,
    teamMembers: [], // Membros do time selecionado
    teamMembersDepartments: [], // IDs de departamentos dos membros

    // Tabela Departamentos
    headers: [
      { text: 'Nome', value: 'name', sortable: true },
      { text: 'Responsável', value: 'managerName', sortable: true },
      { text: 'Qtd. Usuários', value: 'userCount', sortable: true, align: 'center' },
      { text: 'Status', value: 'active', sortable: true },
      { text: 'Criado em', value: 'createdAt', sortable: true },
      { text: 'Ações', value: 'actions', sortable: false, align: 'end' },
      { text: '', value: 'data-table-expand' },
    ],

    // Tabela Cargos (dentro de departamento)
    jobRoleHeaders: [
      { text: 'Cargo', value: 'title', sortable: true },
      { text: 'Ações', value: 'actions', sortable: false, align: 'center', width: '100px' },
    ],

    // Dialog Departamento
    departmentDialog: false,
    editingDepartment: null,
    validDepartment: false,
    saving: false,
    departmentData: {
      name: '',
      description: '',
      managerId: null,
      active: true,
      initialJobRoles: [], // Cargos iniciais ao criar departamento
    },
    newJobRoleTitle: '', // Campo temporário para adicionar cargo
    editingJobRoleIndex: null, // Índice do cargo sendo editado
    editingJobRoleTitle: '', // Título temporário durante edição

    // Gestores
    managers: [],
    loadingManagers: false,

    // Dialog Cargo
    jobRoleDialog: false,
    selectedDepartment: null,
    editingJobRole: null,
    validJobRole: false,
    savingJobRole: false,
    jobRoleData: {
      title: '',
      departmentId: '',
    },

    // Opções
    statusOptions: [
      { text: 'Ativo', value: true },
      { text: 'Desativado', value: false },
    ],

    // Validações
    rules: {
      required: v => !!v || 'Campo obrigatório',
    },

    // Dialog: Movimentação de Usuários
    moveUsersDialog: false,
    departmentToDeactivate: null,
    usersToMove: [],
    loadingUsersToMove: false,
    moveMode: 'bulk', // 'bulk' ou 'individual'
    bulkTargetDepartmentId: null,
    activeDepartments: [],
    movingUsers: false,
    pendingDeactivation: null, // Guarda o contexto da desativação pendente

    // Dialog: Visualizar Usuários
    usersDialog: false,
    selectedDepartmentForUsers: null,
    departmentUsers: [],
    loadingDepartmentUsers: false,

    // Dialog: Excluir Cargo
    deleteJobRoleDialog: false,
    jobRoleToDelete: null,
    selectedDepartmentForDelete: null,
    deletingJobRole: false,

    // Snackbar
    snackbar: {
      show: false,
      text: '',
      color: 'success',
    },
  }),

  computed: {
    isViewOnlyMode() {
      return this.$store.getters.isViewOnlyMode
    },

    filteredDepartments() {
      let filtered = [...this.departments]

      // Filtrar por status (active)
      if (this.filterStatus !== null && this.filterStatus !== undefined) {
        filtered = filtered.filter(d => d.active === this.filterStatus)
      }

      // Filtrar por time (departamentos que têm usuários do time)
      if (this.filterTeam && this.teamMembersDepartments.length > 0) {
        filtered = filtered.filter(d => this.teamMembersDepartments.includes(d.id))
      }

      return filtered
    },

    canConfirmMove() {
      if (this.usersToMove.length === 0) {
        return true // Permite desativar se não houver usuários
      }

      if (this.moveMode === 'bulk') {
        // No modo bulk, precisa ter selecionado um departamento destino
        return !!this.bulkTargetDepartmentId
      } else {
        // No modo individual, todos os usuários devem ter um departamento destino
        return this.usersToMove.every(user => !!user.targetDepartmentId)
      }
    },
  },

  watch: {
    expanded(newVal, oldVal) {
      console.log('👁️ Expanded mudou:', { newVal, oldVal })
      
      // Se expandiu uma linha (newVal tem item), carregar os cargos
      if (newVal && newVal.length > 0) {
        const department = newVal[0]
        console.log('⬇️ Linha expandida, carregando cargos para:', department.name)
        this.loadJobRoles(department)
      }
    },
  },

  mounted() {
    this.loadDepartments()
    this.loadTeams()
  },

  methods: {
    async loadTeams() {
      this.loadingTeams = true
      try {
        console.log('🏢 Carregando times...')
        const response = await TeamsService.list()
        this.teams = response.data || []
        console.log('✅ Times carregados:', this.teams.length)
      } catch (error) {
        console.error('❌ Erro ao carregar times:', error)
        this.showSnackbar('Erro ao carregar times', 'error')
      } finally {
        this.loadingTeams = false
      }
    },

    async onTeamFilterChange(teamId) {
      console.log('🔍 Time selecionado:', teamId)
      
      if (!teamId) {
        // Limpar filtro
        this.teamMembers = []
        this.teamMembersDepartments = []
        return
      }

      try {
        // Buscar membros do time
        const response = await TeamsService.getMembers(teamId)
        this.teamMembers = response.data || []
        
        console.log('👥 Membros do time:', this.teamMembers.length)
        
        // Extrair IDs únicos de departamentos dos membros
        const departmentIds = new Set()
        this.teamMembers.forEach(member => {
          if (member.department?.id) {
            departmentIds.add(member.department.id)
          } else if (member.departmentId) {
            departmentIds.add(member.departmentId)
          }
        })
        
        this.teamMembersDepartments = Array.from(departmentIds)
        console.log('🏢 Departamentos dos membros:', this.teamMembersDepartments)
        
        if (this.teamMembersDepartments.length === 0) {
          this.showSnackbar('Nenhum membro deste time possui departamento definido', 'info')
        }
      } catch (error) {
        console.error('❌ Erro ao carregar membros do time:', error)
        this.showSnackbar('Erro ao carregar membros do time', 'error')
      }
    },

    clearFilters() {
      this.search = ''
      this.filterStatus = null
      this.filterTeam = null
      this.teamMembers = []
      this.teamMembersDepartments = []
    },

    async loadDepartments() {
      this.loading = true

      try {
        console.log('📊 Carregando departamentos...')
        const response = await OrganizationService.departments.list()
        
        // Mapear dados do backend (agora com userCount e nameManager)
        this.departments = response.data.map(dept => ({
          ...dept,
          managerName: dept.nameManager || '-', // Usar nameManager do backend
          jobRoles: [],
          loadingJobRoles: false,
        }))

        console.log('✅ Departamentos carregados:', this.departments.length)
      } catch (error) {
        console.error('❌ Erro ao carregar departamentos:', error)
        this.showSnackbar('Erro ao carregar departamentos', 'error')
      } finally {
        this.loading = false
      }
    },

    async loadJobRoles(department) {
      // Verificar se já carregou os cargos
      if (department.jobRoles && department.jobRoles.length > 0) {
        console.log('✅ Cargos já carregados, pulando requisição')
        return
      }

      department.loadingJobRoles = true

      try {
        console.log(`📋 Carregando cargos do departamento: ${department.name}`)
        console.log(`🔑 Department ID: ${department.id}`)
        
        const response = await OrganizationService.jobRoles.getByDepartment(department.id)
        
        console.log(`✅ Resposta da API:`, response.data)
        console.log(`✅ Total de cargos: ${response.data.length}`)
        
        // Usar Vue.set para garantir reatividade
        this.$set(department, 'jobRoles', response.data)
      } catch (error) {
        console.error('❌ Erro ao carregar cargos:', error)
        console.error('❌ Detalhes do erro:', error.response?.data)
        this.showSnackbar('Erro ao carregar cargos', 'error')
        this.$set(department, 'jobRoles', [])
      } finally {
        department.loadingJobRoles = false
      }
    },

    // === DEPARTAMENTO: CRUD ===
    async openCreateDepartmentDialog() {
      this.editingDepartment = null
      this.departmentData = {
        name: '',
        description: '',
        managerId: null,
        active: true,
        status: 'ACTIVE',
        initialJobRoles: [],
      }
      this.newJobRoleTitle = ''
      this.departmentDialog = true
      
      // Carregar gestores
      await this.loadManagers()
    },

    addInitialJobRole() {
      if (this.newJobRoleTitle && this.newJobRoleTitle.trim()) {
        this.departmentData.initialJobRoles.push(this.newJobRoleTitle.trim())
        this.newJobRoleTitle = ''
      }
    },

    startEditJobRole(index) {
      this.editingJobRoleIndex = index
      this.editingJobRoleTitle = this.departmentData.initialJobRoles[index]
    },

    saveEditJobRole(index) {
      if (this.editingJobRoleTitle && this.editingJobRoleTitle.trim()) {
        this.departmentData.initialJobRoles[index] = this.editingJobRoleTitle.trim()
        this.editingJobRoleIndex = null
        this.editingJobRoleTitle = ''
      }
    },

    cancelEditJobRole() {
      this.editingJobRoleIndex = null
      this.editingJobRoleTitle = ''
    },

    removeInitialJobRole(index) {
      this.departmentData.initialJobRoles.splice(index, 1)
    },

    async loadManagers() {
      this.loadingManagers = true
      
      try {
        console.log('👔 Carregando gestores...')
        const response = await OrganizationService.departments.getManagers()
        this.managers = response.data
        console.log('✅ Gestores carregados:', this.managers.length)
      } catch (error) {
        console.error('❌ Erro ao carregar gestores:', error)
        this.showSnackbar('Erro ao carregar gestores', 'error')
        this.managers = []
      } finally {
        this.loadingManagers = false
      }
    },

    async toggleDepartmentStatus(department) {
      const previousStatus = department.active
      
      // Se está tentando DESATIVAR (de true para false), verificar usuários
      if (previousStatus === true) {
        console.log('🔒 Tentando desativar departamento, verificando usuários...')
        
        // Se não tem usuários, desativar diretamente sem dialog
        if (!department.userCount || department.userCount === 0) {
          console.log('✅ Departamento sem usuários, desativando diretamente...')
          
          try {
            department.active = false
            
            await OrganizationService.departments.update(department.id, { 
              active: false 
            })
            
            console.log('✅ Departamento desativado com sucesso')
            this.showSnackbar('Departamento desativado com sucesso', 'success')
            
            await this.loadDepartments()
            return
          } catch (error) {
            console.error('❌ Erro ao desativar departamento:', error)
            department.active = true
            this.showSnackbar('Erro ao desativar departamento', 'error')
            return
          }
        }
        
        // Se tem usuários, abrir dialog de movimentação
        await this.openMoveUsersDialog(department, 'toggle')
        return
      }

      // Se está ATIVANDO (de false para true), pode ativar direto
      try {
        // Alternar status otimisticamente
        department.active = !department.active
        
        console.log(`🔄 Alterando status do departamento ${department.id} para:`, department.active)
        
        // Chamar API com payload apenas contendo active
        await OrganizationService.departments.update(department.id, { 
          active: department.active 
        })
        
        console.log('✅ Status atualizado com sucesso')
        this.showSnackbar(
          department.active ? 'Departamento ativado' : 'Departamento desativado',
          'success'
        )
        
        // Recarregar departamentos para atualizar userCount
        await this.loadDepartments()
      } catch (error) {
        // Reverter em caso de erro
        console.error('❌ Erro ao alterar status do departamento:', error)
        department.active = previousStatus
        this.showSnackbar('Erro ao alterar status', 'error')
      }
    },

    expandDepartment(department) {
      const idx = this.expanded.indexOf(department)
      if (idx > -1) {
        this.expanded.splice(idx, 1)
      } else {
        this.expanded = [department]
        if (!department.jobRoles) {
          this.loadJobRoles(department)
        }
      }
    },

    editDepartment(department) {
      this.editingDepartment = department
      this.departmentData = {
        name: department.name,
        description: department.description,
        managerId: department.managerId,
        active: department.active !== undefined ? department.active : true,
      }
      this.departmentDialog = true
      
      // Carregar gestores
      this.loadManagers()
    },

    async saveDepartment() {
      if (!this.$refs.departmentForm.validate()) {
        return
      }

      // Validar cargos iniciais ao criar
      if (!this.editingDepartment && this.departmentData.initialJobRoles.length === 0) {
        this.showSnackbar('Adicione pelo menos 1 cargo para o departamento', 'warning')
        return
      }

      this.saving = true

      try {
        if (this.editingDepartment) {
          // Verificar se está tentando DESATIVAR um departamento ATIVO
          const wasActive = this.editingDepartment.active
          const willBeInactive = !this.departmentData.active
          
          if (wasActive && willBeInactive) {
            console.log('🔒 Tentando desativar departamento via dialog, verificando usuários...')
            
            // Se não tem usuários, desativar diretamente sem dialog
            if (!this.editingDepartment.userCount || this.editingDepartment.userCount === 0) {
              console.log('✅ Departamento sem usuários, desativando diretamente...')
              
              const updateData = {
                name: this.departmentData.name,
                description: this.departmentData.description,
                managerId: this.departmentData.managerId,
                active: false,
              }
              
              await OrganizationService.departments.update(this.editingDepartment.id, updateData)
              
              this.showSnackbar('Departamento desativado com sucesso', 'success')
              await this.loadDepartments()
              this.closeDepartmentDialog()
              return
            }
            
            // Se tem usuários, abrir dialog de movimentação
            this.saving = false
            await this.openMoveUsersDialog(this.editingDepartment, 'edit')
            return
          }
          
          // Atualizar departamento (se não está desativando OU já passou pela movimentação)
          console.log('📝 Atualizar departamento:', this.editingDepartment.id, this.departmentData)
          
          const updateData = {
            name: this.departmentData.name,
            description: this.departmentData.description,
            managerId: this.departmentData.managerId,
            active: this.departmentData.active,
          }
          
          const response = await OrganizationService.departments.update(this.editingDepartment.id, updateData)
          console.log('✅ Departamento atualizado - Response:', response)
          console.log('✅ Status:', response.status)
          
          // Recarregar a lista
          console.log('🔄 Recarregando lista de departamentos...')
          await this.loadDepartments()
          console.log('✅ Lista recarregada!')
          
          this.showSnackbar('Departamento atualizado com sucesso!', 'success')
        } else {
          // Criar departamento
          console.log('✨ Criar novo departamento:', this.departmentData)
          const departmentResponse = await OrganizationService.departments.create(this.departmentData)
          const createdDepartment = departmentResponse.data
          
          console.log('✅ Departamento criado:', createdDepartment)
          
          // Criar cargos iniciais
          console.log('💼 Criando cargos iniciais...')
          const jobRolePromises = this.departmentData.initialJobRoles.map(jobRoleTitle => 
            OrganizationService.jobRoles.create({
              title: jobRoleTitle,
              departmentId: createdDepartment.id,
            })
          )
          
          await Promise.all(jobRolePromises)
          console.log('✅ Cargos criados com sucesso!')
          
          // Recarregar a lista completa para garantir dados atualizados
          await this.loadDepartments()

          this.showSnackbar(
            `Departamento criado com ${this.departmentData.initialJobRoles.length} cargo(s)!`,
            'success'
          )
        }

        // Fechar dialog após sucesso (edição ou criação)
        console.log('🚪 Fechando dialog após sucesso...')
        this.closeDepartmentDialog()
        console.log('✅ Dialog fechado!')
      } catch (error) {
        console.error('❌ Erro ao salvar departamento:', error)
        console.error('❌ Detalhes do erro:', error.response)
        this.showSnackbar(
          error.response?.data?.message || 'Erro ao salvar departamento',
          'error'
        )
      } finally {
        console.log('🏁 Finalizando - setting saving = false')
        this.saving = false
      }
    },

    closeDepartmentDialog() {
      console.log('🚪 closeDepartmentDialog chamado')
      this.departmentDialog = false
      this.editingDepartment = null
      this.newJobRoleTitle = ''
      this.editingJobRoleIndex = null
      this.editingJobRoleTitle = ''
      this.departmentData = {
        name: '',
        description: '',
        managerId: null,
        active: true,
        initialJobRoles: [],
      }
      this.$refs.departmentForm?.resetValidation()
      console.log('✅ Dialog fechado e formulário resetado')
    },

    // === CARGO: CRUD ===
    openCreateJobRoleDialog(department) {
      this.selectedDepartment = department
      this.editingJobRole = null
      this.jobRoleData = {
        title: '',
        departmentId: department.id,
      }
      this.jobRoleDialog = true
    },

    editJobRole(department, jobRole) {
      this.selectedDepartment = department
      this.editingJobRole = jobRole
      this.jobRoleData = {
        title: jobRole.title,
        departmentId: department.id,
      }
      this.jobRoleDialog = true
    },

    async saveJobRole() {
      if (!this.$refs.jobRoleForm.validate()) {
        return
      }

      this.savingJobRole = true

      try {
        if (this.editingJobRole) {
          // Atualizar cargo
          console.log('📝 Atualizar cargo:', this.editingJobRole.id, this.jobRoleData)
          
          const response = await OrganizationService.jobRoles.update(
            this.editingJobRole.id,
            this.jobRoleData
          )
          
          // Atualizar na lista local
          const jobRoleIndex = this.selectedDepartment.jobRoles.findIndex(
            jr => jr.id === this.editingJobRole.id
          )
          if (jobRoleIndex !== -1) {
            this.$set(this.selectedDepartment.jobRoles, jobRoleIndex, response.data)
          }
          
          this.showSnackbar('Cargo atualizado com sucesso!', 'success')
        } else {
          // Criar
          console.log('✨ Criar novo cargo:', this.jobRoleData)
          const response = await OrganizationService.jobRoles.create(this.jobRoleData)
          
          // Adicionar à lista de cargos do departamento
          if (!this.selectedDepartment.jobRoles) {
            this.selectedDepartment.jobRoles = []
          }
          this.selectedDepartment.jobRoles.push(response.data)

          this.showSnackbar('Cargo criado com sucesso!', 'success')
        }

        this.closeJobRoleDialog()
      } catch (error) {
        console.error('❌ Erro ao salvar cargo:', error)
        this.showSnackbar(
          error.response?.data?.message || 'Erro ao salvar cargo',
          'error'
        )
      } finally {
        this.savingJobRole = false
      }
    },

    deleteJobRole(department, jobRole) {
      console.log('🗑️ Abrindo dialog de confirmação de exclusão:', jobRole.title)
      this.selectedDepartmentForDelete = department
      this.jobRoleToDelete = jobRole
      this.deleteJobRoleDialog = true
    },

    cancelDeleteJobRole() {
      this.deleteJobRoleDialog = false
      this.jobRoleToDelete = null
      this.selectedDepartmentForDelete = null
    },

    async confirmDeleteJobRole() {
      if (!this.jobRoleToDelete) return

      this.deletingJobRole = true

      try {
        console.log('❌ Excluindo cargo:', this.jobRoleToDelete.id)
        
        await OrganizationService.jobRoles.delete(this.jobRoleToDelete.id)
        
        // Remover da lista local
        const jobRoleIndex = this.selectedDepartmentForDelete.jobRoles.findIndex(
          jr => jr.id === this.jobRoleToDelete.id
        )
        
        if (jobRoleIndex !== -1) {
          this.selectedDepartmentForDelete.jobRoles.splice(jobRoleIndex, 1)
        }
        
        console.log('✅ Cargo excluído com sucesso')
        this.showSnackbar('Cargo excluído com sucesso!', 'success')
        
        this.deleteJobRoleDialog = false
      } catch (error) {
        console.error('❌ Erro ao excluir cargo:', error)
        this.showSnackbar(
          error.response?.data?.message || 'Erro ao excluir cargo',
          'error'
        )
      } finally {
        this.deletingJobRole = false
        this.jobRoleToDelete = null
        this.selectedDepartmentForDelete = null
      }
    },

    closeJobRoleDialog() {
      this.jobRoleDialog = false
      this.selectedDepartment = null
      this.editingJobRole = null
      this.$refs.jobRoleForm?.resetValidation()
    },

    // === VISUALIZAR USUÁRIOS DO DEPARTAMENTO ===
    async showDepartmentUsers(department) {
      console.log('👥 Abrindo modal de usuários do departamento:', department.name)
      
      this.selectedDepartmentForUsers = department
      this.usersDialog = true
      this.loadingDepartmentUsers = true
      this.departmentUsers = []
      
      try {
        console.log('📥 Carregando usuários do departamento:', department.id)
        const response = await OrganizationService.users.getByDepartment(department.id)
        this.departmentUsers = response.data || []
        console.log(`✅ ${this.departmentUsers.length} usuário(s) carregado(s)`)
      } catch (error) {
        console.error('❌ Erro ao carregar usuários:', error)
        this.showSnackbar('Erro ao carregar usuários do departamento', 'error')
      } finally {
        this.loadingDepartmentUsers = false
      }
    },

    // === MOVIMENTAÇÃO DE USUÁRIOS ===
    async openMoveUsersDialog(department, source) {
      console.log('🚀 Abrindo dialog de movimentação para:', department.name, 'source:', source)
      
      this.departmentToDeactivate = department
      this.pendingDeactivation = { department, source }
      this.moveUsersDialog = true
      this.loadingUsersToMove = true
      this.usersToMove = []
      this.moveMode = 'bulk'
      this.bulkTargetDepartmentId = null
      
      try {
        // Carregar usuários do departamento
        console.log('👥 Carregando usuários do departamento:', department.id)
        const usersResponse = await OrganizationService.users.getByDepartment(department.id)
        this.usersToMove = (usersResponse.data || []).map(user => ({
          ...user,
          targetDepartmentId: null, // Para modo individual
        }))
        
        console.log(`✅ ${this.usersToMove.length} usuário(s) carregado(s)`)
        
        // Carregar departamentos ativos (excluindo o atual)
        console.log('📋 Carregando departamentos ativos...')
        await this.loadDepartments()
        this.activeDepartments = this.departments.filter(d => 
          d.active && d.id !== department.id
        )
        
        console.log(`✅ ${this.activeDepartments.length} departamento(s) ativo(s) disponíveis`)
      } catch (error) {
        console.error('❌ Erro ao carregar dados:', error)
        this.showSnackbar('Erro ao carregar usuários do departamento', 'error')
        this.moveUsersDialog = false
      } finally {
        this.loadingUsersToMove = false
      }
    },

    cancelMoveUsers() {
      console.log('❌ Movimentação cancelada pelo usuário')
      
      // Forçar atualização reativa do departamento para corrigir o toggle
      if (this.pendingDeactivation?.department) {
        const dept = this.pendingDeactivation.department
        const index = this.departments.findIndex(d => d.id === dept.id)
        if (index !== -1) {
          // Garantir que o status seja ativo
          this.$set(this.departments[index], 'active', true)
        }
      }
      
      // Incrementar key para forçar re-render dos toggles
      this.toggleRefreshKey++
      
      this.moveUsersDialog = false
      this.departmentToDeactivate = null
      this.usersToMove = []
      this.pendingDeactivation = null
    },

    async confirmMoveUsers() {
      console.log('✅ Confirmando movimentação de usuários...')
      
      this.movingUsers = true
      
      try {
        // Definir quais usuários serão movidos e para onde
        let movementPlan = []
        
        if (this.moveMode === 'bulk') {
          // Mover todos para o mesmo departamento
          movementPlan = this.usersToMove.map(user => ({
            userId: user.id,
            newDepartmentId: this.bulkTargetDepartmentId,
          }))
          console.log(`📦 Modo BULK: Movendo ${movementPlan.length} usuários para departamento ${this.bulkTargetDepartmentId}`)
        } else {
          // Mover individualmente
          movementPlan = this.usersToMove.map(user => ({
            userId: user.id,
            newDepartmentId: user.targetDepartmentId,
          }))
          console.log(`📋 Modo INDIVIDUAL: Movendo ${movementPlan.length} usuários para departamentos variados`)
        }
        
        // Executar movimentações em paralelo
        const updatePromises = movementPlan.map(({ userId, newDepartmentId }) => 
          UsersService.update(userId, { departmentId: newDepartmentId })
        )
        
        await Promise.all(updatePromises)
        console.log('✅ Todos os usuários foram movidos com sucesso!')
        
        // Agora desativar o departamento
        await this.performDeactivation()
        
        // Fechar dialog de movimentação
        this.moveUsersDialog = false
        
        this.showSnackbar(
          `${movementPlan.length} usuário(s) movido(s) e departamento desativado com sucesso!`,
          'success'
        )
        
        // Recarregar departamentos
        await this.loadDepartments()
        
        // Se veio do dialog de edição, fechar ele também
        if (this.pendingDeactivation?.source === 'edit') {
          this.closeDepartmentDialog()
        }
      } catch (error) {
        console.error('❌ Erro ao mover usuários:', error)
        this.showSnackbar(
          error.response?.data?.message || 'Erro ao mover usuários',
          'error'
        )
      } finally {
        this.movingUsers = false
        this.pendingDeactivation = null
      }
    },

    async confirmDeactivateWithoutUsers() {
      console.log('✅ Confirmando desativação sem usuários...')
      
      this.movingUsers = true
      
      try {
        await this.performDeactivation()
        
        this.moveUsersDialog = false
        this.showSnackbar('Departamento desativado com sucesso!', 'success')
        
        await this.loadDepartments()
        
        // Se veio do dialog de edição, fechar ele também
        if (this.pendingDeactivation?.source === 'edit') {
          this.closeDepartmentDialog()
        }
      } catch (error) {
        console.error('❌ Erro ao desativar departamento:', error)
        this.showSnackbar('Erro ao desativar departamento', 'error')
      } finally {
        this.movingUsers = false
        this.pendingDeactivation = null
      }
    },

    async performDeactivation() {
      const department = this.pendingDeactivation.department
      
      console.log('🔒 Desativando departamento:', department.id)
      
      await OrganizationService.departments.update(department.id, { 
        active: false 
      })
      
      console.log('✅ Departamento desativado com sucesso')
    },

    // === HELPERS ===
    getDeptColor(item) {
      const palette = ['#22d3ee', '#a855f7', '#f59e0b', '#4ade80', '#60a5fa', '#fb923c', '#f472b6', '#34d399']
      const idx = this.departments.indexOf(item)
      return palette[idx >= 0 ? idx % palette.length : 0]
    },

    getInitials(name) {
      if (!name) return '??'
      const parts = name.split(' ')
      if (parts.length >= 2) {
        return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase()
      }
      return name.substring(0, 2).toUpperCase()
    },

    formatDate(dateString) {
      if (!dateString) return '-'
      const date = new Date(dateString)
      return date.toLocaleDateString('pt-BR')
    },

    showSnackbar(text, color = 'success') {
      this.snackbar = {
        show: true,
        text,
        color,
      }
    },
  },
}
</script>

<style scoped>
/* Fundo Global Premium */
.dashboard-container {
  background-color: var(--bg-main, #0D0D0D) !important;
  max-width: 1440px;
  margin: 0 auto;
}

/* ── Page ────────────────────────────────── */
.dp-page { padding: 24px 96px; }
/* ── Page ────────────────────────────────── */
.dp-page { padding: 24px 96px; }

/* ── Header ──────────────────────────────── */
.dp-header-icon {
  width: 44px; height: 44px; border-radius: 10px;
  display: flex; align-items: center; justify-content: center;
  background: rgba(34,211,238,0.12); flex-shrink: 0;
}
.dp-page-title { font-size: 22px; font-weight: 700; letter-spacing: -0.01em; line-height: 1.2; }
.dp-page-sub { font-size: 13px; margin-top: 2px; }
.theme--dark .dp-page-title { color: #f5f5f5; }
.theme--light .dp-page-title { color: #1D1D1F; }
.theme--dark .dp-page-sub { color: rgba(255,255,255,0.45); }
.theme--light .dp-page-sub { color: rgba(0,0,0,0.5); }

/* ── Buttons ─────────────────────────────── */
.dp-btn-new { background-color: #22d3ee !important; color: #06222b !important; font-weight: 700 !important; border-radius: 8px !important; }
.dp-btn-clear { border-radius: 8px !important; font-weight: 500 !important; white-space: nowrap; }
.theme--dark .dp-btn-clear { border-color: #2a2a2a !important; color: rgba(255,255,255,0.55) !important; }
.theme--light .dp-btn-clear { border-color: #e0e0e0 !important; color: rgba(0,0,0,0.55) !important; }

/* ── Filters ─────────────────────────────── */
.dp-filters { flex-wrap: nowrap; }
.dp-search { min-width: 0; }
.dp-filter { flex-shrink: 0; max-width: 160px; }

/* ── Table ───────────────────────────────── */
.dp-table { border-radius: 10px !important; overflow: hidden; }
.theme--dark .dp-table { background: transparent !important; }
.theme--light .dp-table { background: #ffffff !important; }

.dp-table ::v-deep thead tr th {
  font-size: 11px !important; font-weight: 700 !important;
  letter-spacing: 0.07em !important; text-transform: uppercase !important;
  border-bottom: none !important;
}
.theme--dark .dp-table ::v-deep thead tr { background-color: #0d0d0d !important; }
.theme--dark .dp-table ::v-deep thead tr th { color: rgba(255,255,255,0.4) !important; }
.theme--light .dp-table ::v-deep thead tr { background-color: #f8f8f8 !important; }
.theme--light .dp-table ::v-deep thead tr th { color: rgba(0,0,0,0.45) !important; }

.theme--dark .dp-table ::v-deep tbody tr { background-color: #111111 !important; }
.theme--dark .dp-table ::v-deep tbody tr:hover { background-color: #161616 !important; }
.theme--dark .dp-table ::v-deep tbody tr td { border-bottom: 1px solid #1a1a1a !important; }
.theme--light .dp-table ::v-deep tbody tr:hover { background-color: #fafafa !important; }
.theme--light .dp-table ::v-deep tbody tr td { border-bottom: 1px solid #f0f0f0 !important; }

/* ── Dept icon ───────────────────────────── */
.dp-dept-icon { width: 32px; height: 32px; border-radius: 7px; display: flex; align-items: center; justify-content: center; flex-shrink: 0; }
.dp-dept-name { font-size: 14px; font-weight: 600; }
.dp-dept-sub { font-size: 11.5px; margin-top: 1px; }
.theme--dark .dp-dept-name { color: rgba(255,255,255,0.9); }
.theme--light .dp-dept-name { color: #1D1D1F; }
.theme--dark .dp-dept-sub { color: rgba(255,255,255,0.4); }
.theme--light .dp-dept-sub { color: rgba(0,0,0,0.5); }

/* ── Manager ─────────────────────────────── */
.dp-resp-name { font-size: 13px; }
.theme--dark .dp-resp-name { color: rgba(255,255,255,0.82); }
.theme--light .dp-resp-name { color: #333; }

/* ── Count ───────────────────────────────── */
.dp-count-icon { opacity: 0.5; }
.theme--dark .dp-count-icon { color: rgba(255,255,255,0.5) !important; }
.dp-count-value { font-size: 13px; font-weight: 500; }
.theme--dark .dp-count-value { color: rgba(255,255,255,0.75); }

/* ── Status ──────────────────────────────── */
.dp-status-dot { width: 7px; height: 7px; border-radius: 50%; flex-shrink: 0; display: inline-block; }
.dp-status-text { font-size: 13px; font-weight: 500; }

/* ── Date ────────────────────────────────── */
.dp-date { font-size: 12.5px; }
.theme--dark .dp-date { color: rgba(255,255,255,0.5); }
.theme--light .dp-date { color: rgba(0,0,0,0.5); }

/* ── Toggle ──────────────────────────────── */
.dp-toggle ::v-deep .v-input__slot { margin-bottom: 0 !important; }

/* ── Action buttons ──────────────────────── */
.dp-action-btn { opacity: 0.45; transition: opacity 0.15s; }
.dp-action-btn:hover { opacity: 1; }
.theme--dark .dp-action-btn { color: rgba(255,255,255,0.7) !important; }
.theme--light .dp-action-btn { color: rgba(0,0,0,0.5) !important; }
.dp-action-btn--delete:hover { color: #f87171 !important; }

/* ── Empty ───────────────────────────────── */
.dp-empty { font-size: 12.5px; }
.theme--dark .dp-empty { color: rgba(255,255,255,0.3); }
.theme--light .dp-empty { color: rgba(0,0,0,0.35); }

/* ── Expanded panel ──────────────────────── */
.dp-expanded-panel { border-top: 1px solid transparent; }
.theme--dark .dp-expanded-panel { background: #0d0d0d !important; border-color: #1a1a1a; }
.theme--light .dp-expanded-panel { background: #f8f8f8 !important; border-color: #eeeeee; }
.dp-expanded-title { font-size: 13px; font-weight: 600; }
.theme--dark .dp-expanded-title { color: rgba(255,255,255,0.75); }

/* Job role sub-table */
.theme--dark .dp-jobrole-table { background: transparent !important; }
.theme--dark .dp-jobrole-table ::v-deep tbody tr { background: transparent !important; }
.theme--dark .dp-jobrole-table ::v-deep tbody tr td { border-bottom: 1px solid #1a1a1a !important; }
.theme--dark .dp-jobrole-table ::v-deep thead tr th { color: rgba(255,255,255,0.3) !important; background: transparent !important; }

/* Footer pagination */
.theme--dark .dp-table ::v-deep .v-data-footer { background-color: #0d0d0d !important; border-top: 1px solid #1a1a1a !important; color: rgba(255,255,255,0.5) !important; }
.dp-table ::v-deep .v-data-table__wrapper { overflow-x: auto; }
</style>

